import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, FormControl, InputLabel, Select, MenuItem, Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { BRAND } from '../../theme/tokens';

interface Props {
  open: boolean;
  jobRef: string;
  jobTitle?: string;
  onClose: () => void;
}

/** Raise a draft PO tied to the current job, then open PO Print to add lines. */
export const RaiseJobPoDialog: React.FC<Props> = ({ open, jobRef, jobTitle, onClose }) => {
  const navigate = useNavigate();
  const { suppliers, createPurchaseOrder } = useData();
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '');

  useEffect(() => {
    if (open && suppliers.length > 0 && !supplierId) {
      setSupplierId(suppliers[0].id);
    }
  }, [open, suppliers, supplierId]);

  const handleRaise = () => {
    if (!supplierId || !jobRef) return;
    const poId = createPurchaseOrder({
      supplierId,
      jobRef,
      reference: jobTitle || jobRef,
    });
    onClose();
    navigate(`/po-print/${poId}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Raise purchase order</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>
          Creates a draft PO linked to this job — you add lines on the PO page.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, backgroundColor: '#f5f0ff', border: `1px solid ${BRAND}33` }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#555', lineHeight: 1.5 }}>
            Job <strong>{jobRef}</strong>
            {jobTitle ? <> · {jobTitle}</> : null}
            {' '}— appears in billable items once raised. Reconcile the supplier bill before invoicing.
          </Typography>
        </Box>
        <FormControl fullWidth size="small">
          <InputLabel>Supplier</InputLabel>
          <Select value={supplierId} label="Supplier" onChange={e => setSupplierId(e.target.value)}>
            {suppliers.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleRaise}
          disabled={!supplierId || !jobRef}
          sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700,
            '&:hover': { backgroundColor: '#4a3580' } }}
        >
          Raise PO &amp; open
        </Button>
      </DialogActions>
    </Dialog>
  );
};
